import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, BarChart3, RefreshCw, ChevronDown } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";

const API = window.location.origin + "/api";

const fmtEuroShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1000) return `${(n / 1000).toFixed(1)}k €`;
  return `${Math.round(n || 0)} €`;
};

const fmtEuroFull = (n) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDayShort = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}`;
};

const fmtMonthShort = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.slice(0, 7).split("-");
  const names = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  return `${names[parseInt(m, 10) - 1] || m} ${y.slice(2)}`;
};

// Tooltip come componente TOP-LEVEL (fuori da PipelineTrendChart) per
// evitare che React lo ricrei ad ogni render → warning
// "no-unstable-nested-components" e reset DOM/state della subtree.
function TrendTooltip({ active, payload, label, stages, granularity }) {
  if (!active || !payload || !payload.length) return null;
  const sorted = [...payload]
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value);
  const displayLabel = granularity === "month"
    ? fmtMonthShort(label)
    : label;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-3 text-xs min-w-[220px]">
      <p className="font-bold text-slate-700 mb-2">{displayLabel}</p>
      {sorted.length === 0 ? (
        <p className="text-slate-400">Nessun valore</p>
      ) : (
        <div className="space-y-1">
          {sorted.map((p) => {
            const st = stages.find((s) => s.id === p.dataKey);
            return (
              <div key={p.dataKey} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-slate-600 truncate">
                  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.stroke }} />
                  <span className="truncate">{st?.name || p.dataKey}</span>
                </span>
                <span className="font-semibold text-slate-800 tabular-nums">{fmtEuroFull(p.value)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Grafico linee — andamento € giornaliero per stage di una pipeline.
 * Data range: dalla prima data disponibile in `opp_stage_history` a oggi
 * (di default). L'utente può cambiare pipeline via tendina.
 */
export default function PipelineTrendChart() {
  const [pipelines, setPipelines] = useState([]);
  const [selectedPid, setSelectedPid] = useState(null);
  const [granularity, setGranularity] = useState("day"); // 'day' | 'month'
  // Default: mese corrente in formato YYYY-MM. L'utente può passare
  // a "Tutti i mesi" o a un altro mese specifico dalla tendina.
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiddenStages, setHiddenStages] = useState({}); // {sid: true}

  // Carica pipelines dal Kanban endpoint (già include struttura + counts)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/piani-cura/pipelines?light=1`, { withCredentials: true });
        const list = (res.data?.pipelines || []).filter((p) => (p.stages || []).length);
        setPipelines(list);
        if (list.length && !selectedPid) setSelectedPid(list[0].id);
      } catch (e) {
        setPipelines([]);
      }
    })();
  }, []);

  const fetchTrend = useCallback(async (pid, gran, mo) => {
    if (!pid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ pipeline_id: pid, granularity: gran || "day" });
      if (mo) params.set("month", mo);
      const res = await axios.get(
        `${API}/dashboard/pipeline-trend?${params.toString()}`,
        { withCredentials: true },
      );
      setData(res.data);
      setHiddenStages({});
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedPid) fetchTrend(selectedPid, granularity, monthFilter);
  }, [selectedPid, granularity, monthFilter, fetchTrend]);

  // Se cambio in vista mensile, resetta il filtro mese (mostra tutti)
  useEffect(() => { if (granularity === "month") setMonthFilter(""); }, [granularity]);

  const stages = data?.stages || [];
  const series = data?.series || [];
  const monthsAvailable = data?.months_available || [];

  // Label leggibile per il mese (es "2026-06" → "giugno 2026")
  const monthLabel = (ym) => {
    if (!ym || ym.length < 7) return ym;
    const [y, m] = ym.split("-");
    const names = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
    return `${names[parseInt(m, 10) - 1] || m} ${y}`;
  };

  // Filtra solo le linee visibili (toggle da legend click)
  const visibleStages = useMemo(
    () => stages.filter((s) => !hiddenStages[s.id]),
    [stages, hiddenStages],
  );

  const toggleStage = (sid) => {
    setHiddenStages((prev) => ({ ...prev, [sid]: !prev[sid] }));
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="pipeline-trend-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-500" /> Andamento € per stage
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented: Giornaliero / Mensile */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            <button
              type="button"
              onClick={() => setGranularity("day")}
              className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                granularity === "day" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="granularity-day"
            >
              Giornaliero
            </button>
            <button
              type="button"
              onClick={() => setGranularity("month")}
              className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                granularity === "month" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="granularity-month"
            >
              Mensile
            </button>
          </div>

          {/* Dropdown mese (solo in vista giornaliera) */}
          {granularity === "day" && monthsAvailable.length > 0 && (
            <div className="relative">
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="h-9 pl-3 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
                data-testid="month-filter"
                disabled={loading}
              >
                <option value="">Tutti i mesi</option>
                {monthsAvailable.map((ym) => (
                  <option key={ym} value={ym}>{monthLabel(ym)}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          )}

          <div className="relative">
            <select
              value={selectedPid || ""}
              onChange={(e) => setSelectedPid(e.target.value)}
              className="h-9 pl-3 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
              data-testid="pipeline-trend-selector"
              disabled={loading || !pipelines.length}
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchTrend(selectedPid, granularity, monthFilter)}
            disabled={loading}
            data-testid="pipeline-trend-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {data?.earliest_available && (
        <p className="text-[11px] text-slate-500 mb-3">
          Dati disponibili da <b className="text-slate-700">{fmtDayShort(data.earliest_available)}</b> ({new Date(data.earliest_available).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })})
          · Range: <b className="text-slate-700">{fmtDayShort(data.start_date)}</b> → <b className="text-slate-700">{fmtDayShort(data.end_date)}</b>
        </p>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-400">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </div>
      ) : !series.length ? (
        <div className="py-16 text-center text-slate-400 text-sm">Nessun dato disponibile</div>
      ) : (
        <>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={granularity === "month" ? fmtMonthShort : fmtDayShort}
                  interval="preserveStartEnd"
                  minTickGap={granularity === "month" ? 20 : 40}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={fmtEuroShort}
                />
                <Tooltip content={<TrendTooltip stages={stages} granularity={granularity} />} />
                {visibleStages.map((st) => (
                  <Line
                    key={st.id}
                    type="monotone"
                    dataKey={st.id}
                    stroke={st.color}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                    name={st.name}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Legenda custom con toggle */}
          <div className="flex flex-wrap gap-2 mt-4 justify-center">
            {stages.map((st) => {
              const hidden = hiddenStages[st.id];
              return (
                <button
                  type="button"
                  key={st.id}
                  onClick={() => toggleStage(st.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all ${
                    hidden ? "bg-slate-50 text-slate-400 border-slate-200 line-through" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`legend-toggle-${st.id}`}
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: hidden ? "#cbd5e1" : st.color }} />
                  <span className="truncate max-w-[180px]">{st.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
